import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import {
  Plus, Trash2, Star, Loader2, Wallet, Landmark, Smartphone, AlertCircle, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const PROVIDER_LABEL = {
  instapay: 'InstaPay (e-wallets & banks)',
  pesonet: 'PESONet (banks only)',
};

function iconForBankCode(bankCode) {
  const bc = (bankCode || '').toLowerCase();
  if (bc === 'gcash') return Smartphone;
  if (bc === 'paymaya' || bc === 'maya') return Smartphone;
  return Landmark;
}

/**
 * Member-facing payout method manager. Lists saved methods, allows add/delete,
 * and lets the user mark one as default. Backed by /api/payouts/methods + the
 * PayMongo receiving-institutions lookup.
 */
export function PayoutMethodsCard({ onChange }) {
  const { api, user } = useAuth();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [provider, setProvider] = useState('instapay');
  const [institutions, setInstitutions] = useState([]);
  const [instLoading, setInstLoading] = useState(false);
  const [form, setForm] = useState({
    label: '',
    bank_code: '',
    bank_name: '',
    account_name: '',
    account_number: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const verified = user?.verification_status === 'verified';

  const fetchMethods = useCallback(async () => {
    try {
      const { data } = await api.get('/api/payouts/methods');
      setMethods(data.methods || []);
      if (onChange) onChange(data.methods || []);
    } catch (e) {
      console.error('Failed to load payout methods:', e);
    } finally {
      setLoading(false);
    }
  }, [api, onChange]);

  useEffect(() => { fetchMethods(); }, [fetchMethods]);

  const loadInstitutions = useCallback(async (prov) => {
    setInstLoading(true);
    try {
      const { data } = await api.get(`/api/payouts/receiving-institutions?provider=${prov}`);
      setInstitutions(data.institutions || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'PayMongo not configured yet — ask admin to set the disbursement keys.';
      toast.error(msg);
      setInstitutions([]);
    } finally {
      setInstLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (addOpen) loadInstitutions(provider);
  }, [addOpen, provider, loadInstitutions]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.bank_code || !form.bank_name) {
      toast.warning('Please pick a bank or e-wallet from the list');
      return;
    }
    if (!form.account_name.trim() || !form.account_number.trim()) {
      toast.warning('Account name and number are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/payouts/methods', { ...form, provider });
      toast.success('Payout method added');
      setAddOpen(false);
      setForm({ label: '', bank_code: '', bank_name: '', account_name: '', account_number: '' });
      fetchMethods();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save payout method');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this payout method? Any pending withdrawal using it must complete first.')) return;
    setBusyId(id);
    try {
      await api.delete(`/api/payouts/methods/${id}`);
      toast.success('Payout method removed');
      fetchMethods();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefault = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/api/payouts/methods/${id}/default`);
      toast.success('Default payout method updated');
      fetchMethods();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update default');
    } finally {
      setBusyId(null);
    }
  };

  const pickInstitution = (inst) => {
    setForm((f) => ({
      ...f,
      bank_code: inst.provider_code || inst.id,
      bank_name: inst.name,
      label: f.label || inst.name,
    }));
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="payout-methods-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#00E5FF]" />
            </div>
            <div>
              <h3 className="font-outfit text-lg font-semibold text-white">Linked Payout Methods</h3>
              <p className="text-[#5C7599] text-xs">Add your GCash, PayMaya, or bank to receive automated withdrawals via PayMongo.</p>
            </div>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button
                className="btn-primary"
                disabled={!verified}
                data-testid="add-payout-method-btn"
                title={verified ? '' : 'Complete ID verification first'}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Method
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Add Payout Method</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Network</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['instapay', 'pesonet'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setProvider(p); setForm((f) => ({ ...f, bank_code: '', bank_name: '' })); }}
                        className={`p-3 rounded-md border text-sm font-medium transition-colors ${
                          provider === p
                            ? 'bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]'
                            : 'bg-[#000B18] border-white/20 text-[#A0B4CC] hover:border-white/40'
                        }`}
                        data-testid={`provider-${p}-btn`}
                      >
                        {PROVIDER_LABEL[p]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Bank / E-wallet</Label>
                  {instLoading ? (
                    <div className="flex items-center gap-2 text-[#A0B4CC] text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading institutions…
                    </div>
                  ) : institutions.length === 0 ? (
                    <div className="text-[#A0B4CC] text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-[#FFEA00]" />
                      No institutions available for this network. Try the other network or contact support.
                    </div>
                  ) : (
                    <select
                      value={form.bank_code}
                      onChange={(e) => {
                        const inst = institutions.find((i) => (i.provider_code || i.id) === e.target.value);
                        if (inst) pickInstitution(inst);
                      }}
                      className="w-full p-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm"
                      required
                      data-testid="bank-code-select"
                    >
                      <option value="">— select —</option>
                      {institutions.map((i) => (
                        <option key={i.id} value={i.provider_code || i.id}>{i.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Account Holder Name</Label>
                  <Input
                    value={form.account_name}
                    onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                    placeholder="As printed on your account"
                    className="bg-[#000B18] border-white/20 text-white"
                    required
                    data-testid="payout-account-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">{(form.bank_code || '').toLowerCase().match(/gcash|paymaya|maya/) ? 'Mobile Number' : 'Account Number'}</Label>
                  <Input
                    value={form.account_number}
                    onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    placeholder={(form.bank_code || '').toLowerCase().match(/gcash|paymaya|maya/) ? '09XXXXXXXXX' : 'Bank account number'}
                    className="bg-[#000B18] border-white/20 text-white"
                    required
                    data-testid="payout-account-number"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Label (optional)</Label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder='e.g. "Primary GCash"'
                    className="bg-[#000B18] border-white/20 text-white"
                    maxLength={60}
                    data-testid="payout-label"
                  />
                </div>

                <div className="rounded-md bg-[#00E5FF]/5 border border-[#00E5FF]/20 p-3 text-xs text-[#A0B4CC] flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#00E5FF] mt-0.5 shrink-0" />
                  Account number is stored securely and shown masked. Withdrawals are sent automatically via PayMongo to this account.
                </div>

                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={submitting || !form.bank_code}
                    className="w-full btn-primary"
                    data-testid="submit-payout-method-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Save Payout Method
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {!verified && (
          <div className="mb-4 p-3 rounded-md bg-[#FFEA00]/10 border border-[#FFEA00]/30 text-[#FFEA00] text-xs flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5" />
            Complete ID verification to add a payout method.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#5C7599]" />
          </div>
        ) : methods.length === 0 ? (
          <div className="py-8 text-center text-[#A0B4CC] text-sm">
            <Wallet className="w-10 h-10 mx-auto mb-2 text-[#5C7599] opacity-50" />
            No payout methods yet. Add one to enable automated withdrawals.
          </div>
        ) : (
          <div className="space-y-2" data-testid="payout-methods-list">
            {methods.map((m) => {
              const Icon = iconForBankCode(m.bank_code);
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#000B18] border border-white/5"
                  data-testid={`payout-method-row-${m.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-md bg-[#041A3B] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#00E5FF]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate flex items-center gap-2">
                        <span className="truncate">{m.label || m.bank_name}</span>
                        {m.is_default && (
                          <Badge className="bg-[#00E676]/15 text-[#00E676] border-0 text-[10px]">Default</Badge>
                        )}
                      </div>
                      <p className="text-[#5C7599] text-xs truncate">
                        {m.bank_name} · {m.account_number_masked || '••••'} · {m.account_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!m.is_default && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSetDefault(m.id)}
                        disabled={busyId === m.id}
                        className="text-[#A0B4CC] hover:text-[#00E676] h-8 px-2"
                        data-testid={`set-default-${m.id}`}
                        title="Set as default"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(m.id)}
                      disabled={busyId === m.id}
                      className="text-[#A0B4CC] hover:text-[#FF1744] h-8 px-2"
                      data-testid={`delete-payout-${m.id}`}
                      title="Remove"
                    >
                      {busyId === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
